import { z } from 'zod';

const UINT256_MAX = (1n << 256n) - 1n;
export const uint256StringSchema = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/)
  .pipe(z.string().refine(value => BigInt(value) <= UINT256_MAX, { message: 'Exceeds uint256', abort: true }));
const positiveAmount = uint256StringSchema.pipe(z.string().refine(value => BigInt(value) > 0n, { message: 'Must be positive', abort: true }));
// Solidity launch reserve/supply arithmetic uses uint128 operands and uint256 products.
export const launchAmountSchema = positiveAmount.pipe(z.string().refine(value => BigInt(value) < (1n << 128n), { message: 'Exceeds launch uint128 bound', abort: true }));
export const nonzeroAddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value), 'Zero address is not allowed');
export const bytes32Schema = z.string().regex(/^0x[a-fA-F0-9]{64}$/);
const bps = z.number().int().min(0).max(10_000);
const id = z.string().min(1).max(128);

export const creatorRecipientsSchema = z.array(z.strictObject({
  wallet: nonzeroAddressSchema,
  weightBps: bps.refine(value => value > 0),
})).min(1).max(5).superRefine((recipients, ctx) => {
  if (recipients.reduce((sum, recipient) => sum + recipient.weightBps, 0) !== 10_000)
    ctx.addIssue({ code: 'custom', message: 'Creator weights must total 10000 bps' });
  if (new Set(recipients.map(recipient => recipient.wallet.toLowerCase())).size !== recipients.length)
    ctx.addIssue({ code: 'custom', message: 'Duplicate creator wallet' });
});

export const feePolicySnapshotSchema = z.strictObject({
  schemaVersion: z.literal(1),
  id,
  policyHash: bytes32Schema,
  usage: z.enum(['test-only', 'production-approved']),
  quoteId: id,
  totalSupply: launchAmountSchema,
  tiers: z.array(z.strictObject({ threshold: uint256StringSchema, baseFeeBps: bps })).min(1).max(32),
  creatorShareBps: bps,
  optionalCreatorChargeCapBps: bps,
  lpFeeBps: bps,
  valuationBasis: z.literal('pre-swap-price-times-fixed-supply-in-quote-base-units'),
  feeBasis: z.literal('quote-notional'),
  rounding: z.literal('floor-remainder-to-last-creator-slot'),
}).superRefine((policy, ctx) => {
  if (policy.tiers[0]?.threshold !== '0')
    ctx.addIssue({ code: 'custom', path: ['tiers', 0], message: 'Starting threshold must be zero' });
  for (let index = 0; index < policy.tiers.length; index++) {
    const tier = policy.tiers[index]!;
    const previous = policy.tiers[index - 1];
    if (previous && (BigInt(tier.threshold) <= BigInt(previous.threshold) || tier.baseFeeBps > previous.baseFeeBps))
      ctx.addIssue({ code: 'custom', path: ['tiers', index], message: 'Thresholds must increase and fees must not increase' });
    if (tier.baseFeeBps + policy.optionalCreatorChargeCapBps + policy.lpFeeBps >= 10_000)
      ctx.addIssue({ code: 'custom', path: ['tiers', index], message: 'Combined fee must be below 100%' });
  }
});
export type FeePolicySnapshotV1 = z.infer<typeof feePolicySnapshotSchema>;

export const launchDraftInputSchema = z.strictObject({
  clientRequestId: z.uuid(),
  vault: nonzeroAddressSchema,
  metadata: z.strictObject({
    name: z.string().trim().min(1).max(64),
    symbol: z.string().trim().min(1).max(16).regex(/^[A-Za-z0-9]+$/),
    description: z.string().max(2000).optional(),
    imageUri: z.url({ protocol: /^(https|ipfs)$/ }).max(2048).optional(),
  }),
  quoteAssetId: id,
  presetId: id,
  feePolicyId: id,
  creatorRecipients: creatorRecipientsSchema,
  optionalCreatorChargeBps: bps,
  creatorFirstBuy: z.strictObject({ amountIn: launchAmountSchema, minimumAmountOut: launchAmountSchema }).optional(),
});
export type LaunchDraftInput = z.infer<typeof launchDraftInputSchema>;

export const launchConfigSchema = z.strictObject({
  schemaVersion: z.literal(1),
  chainId: z.literal(4663),
  status: z.enum(['unavailable', 'ready']),
  unavailableReason: z.string().min(1).nullable(),
  quotes: z.array(z.strictObject({ id, symbol: z.string().min(1).max(16), address: nonzeroAddressSchema, decimals: z.number().int().min(0).max(18) })).max(64),
  presets: z.array(z.strictObject({ id, quoteId: id, totalSupply: launchAmountSchema, curveAllocation: launchAmountSchema, lpAllocation: launchAmountSchema, graduationTarget: launchAmountSchema, presetHash: bytes32Schema })).max(128),
  policies: z.array(feePolicySnapshotSchema).max(128),
  releases: z.array(z.strictObject({ id, factory: nonzeroAddressSchema, implementationHash: bytes32Schema })).max(32),
  configuredAt: z.iso.datetime(),
}).superRefine((config, ctx) => {
  for (const [name, entries] of [['quotes', config.quotes], ['presets', config.presets], ['policies', config.policies], ['releases', config.releases]] as const) {
    if (new Set(entries.map(entry => entry.id)).size !== entries.length)
      ctx.addIssue({ code: 'custom', path: [name], message: 'Duplicate ID' });
    if (config.status === 'ready' && entries.length === 0)
      ctx.addIssue({ code: 'custom', path: [name], message: 'Ready configuration cannot be empty' });
  }
  if ((config.status === 'ready') !== (config.unavailableReason === null))
    ctx.addIssue({ code: 'custom', message: 'Readiness must match unavailable reason' });
  const quotes = new Set(config.quotes.map(quote => quote.id));
  for (const preset of config.presets) {
    if (!quotes.has(preset.quoteId) || BigInt(preset.curveAllocation) + BigInt(preset.lpAllocation) !== BigInt(preset.totalSupply))
      ctx.addIssue({ code: 'custom', path: ['presets'], message: 'Unknown quote or allocations do not conserve supply' });
  }
  for (const policy of config.policies) {
    if (!quotes.has(policy.quoteId)) ctx.addIssue({ code: 'custom', path: ['policies'], message: 'Unknown quote' });
    if (config.status === 'ready' && policy.usage !== 'production-approved')
      ctx.addIssue({ code: 'custom', path: ['policies'], message: 'Test policies cannot enable production launches' });
  }
});
export type LaunchConfig = z.infer<typeof launchConfigSchema>;

/** Resolve only server-approved IDs. Parsing a client policy is never approval. */
export function resolveLaunchDraft(configInput: LaunchConfig, draftInput: LaunchDraftInput) {
  const config = launchConfigSchema.parse(configInput);
  const draft = launchDraftInputSchema.parse(draftInput);
  if (config.status !== 'ready') throw new Error('Launch configuration is unavailable');
  const quote = config.quotes.find(item => item.id === draft.quoteAssetId);
  const preset = config.presets.find(item => item.id === draft.presetId);
  const policy = config.policies.find(item => item.id === draft.feePolicyId);
  if (!quote || !preset || !policy) throw new Error('Unknown launch configuration ID');
  if (preset.quoteId !== quote.id || policy.quoteId !== quote.id || policy.totalSupply !== preset.totalSupply)
    throw new Error('Quote, preset and policy are incompatible');
  if (draft.optionalCreatorChargeBps > policy.optionalCreatorChargeCapBps)
    throw new Error('Optional creator charge exceeds approved cap');
  return { draft, quote, preset, policy };
}

/** Projection/reference arithmetic only; execution selects from canonical on-chain state. */
export function selectLaunchFeeTier(policyInput: FeePolicySnapshotV1, valuation: bigint): number {
  const policy = feePolicySnapshotSchema.parse(policyInput);
  uint256StringSchema.parse(valuation.toString());
  let selected = 0;
  for (let index = 1; index < policy.tiers.length; index++) {
    if (valuation < BigInt(policy.tiers[index]!.threshold)) break;
    selected = index;
  }
  return selected;
}

/** Reserves and supply use token base units; result is quote base units, rounded down. */
export function reserveValuation(virtualQuote: bigint, virtualBase: bigint, supply: bigint): bigint {
  for (const value of [virtualQuote, virtualBase, supply]) positiveAmount.parse(value.toString());
  const result = virtualQuote * supply / virtualBase;
  uint256StringSchema.parse(result.toString());
  return result;
}

/** Allocates an already earned creator bucket, never charges the trader again. */
export function splitCreatorEarnings(amount: bigint, recipientsInput: z.infer<typeof creatorRecipientsSchema>): bigint[] {
  uint256StringSchema.parse(amount.toString());
  const recipients = creatorRecipientsSchema.parse(recipientsInput);
  let allocated = 0n;
  return recipients.map((recipient, index) => {
    const share = index === recipients.length - 1 ? amount - allocated : amount * BigInt(recipient.weightBps) / 10_000n;
    allocated += share;
    return share;
  });
}

/** Only the earned platform remainder AFTER third-party obligations may enter here. */
export function allocatePlatformRemainder(amount: bigint): { buybackBurn: bigint; team: bigint } {
  uint256StringSchema.parse(amount.toString());
  const buybackBurn = amount * 8_000n / 10_000n;
  return { buybackBurn, team: amount - buybackBurn };
}
